import type { GadgetDef, ProjectileDef } from '@vm/shared';

const bomblet: ProjectileDef = {
  speed: 8,
  gravity: 1,
  radius: 0.12,
  fuse: 0.9,
  bounce: 0.35,
  sticky: false,
  impactDamage: 0,
  visual: 'grenade',
  explosion: { radius: 2.2, damage: 55, voxelPower: 40, voxelRadius: 0.8, knockback: 6, selfDamage: 0.5 },
};

const LIST: Omit<GadgetDef, 'num'>[] = [
  {
    id: 'frag', name: 'Frag Grenade', description: 'Bouncy grenade with a 2 second fuse.', kind: 'frag',
    cooldown: 12, unlockLevel: 1, price: 0, currency: 'coins', icon: 'bomb', color: 0x5a7a3a,
    projectile: {
      speed: 18, gravity: 1, radius: 0.14, fuse: 2.1, bounce: 0.4, sticky: false, impactDamage: 5, visual: 'grenade',
      explosion: { radius: 3.5, damage: 100, voxelPower: 60, voxelRadius: 1.25, knockback: 11, selfDamage: 0.5 },
    },
  },
  {
    id: 'medkit', name: 'Repair Kit', description: 'Rebuilds 60 health worth of voxels over 3 seconds.', kind: 'medkit',
    cooldown: 18, unlockLevel: 2, price: 600, currency: 'coins', amount: 60, duration: 3, icon: 'heart-pulse', color: 0x3aa850,
  },
  {
    id: 'stim', name: 'Speed Stim', description: '+35% movement speed for 5 seconds.', kind: 'stim',
    cooldown: 16, unlockLevel: 4, price: 900, currency: 'coins', amount: 0.35, duration: 5, icon: 'zap', color: 0xf5d23a,
  },
  {
    id: 'wall', name: 'Pop-up Wall', description: 'Instantly deploys a steel wall in front of you.', kind: 'wall',
    cooldown: 20, unlockLevel: 5, price: 1800, currency: 'coins', amount: 24, duration: 0, icon: 'brick-wall', color: 0x7a8594,
  },
  {
    id: 'sticky', name: 'Sticky Bomb', description: 'Sticks to walls and players. Bigger blast.', kind: 'sticky',
    cooldown: 15, unlockLevel: 6, price: 1500, currency: 'coins', icon: 'target', color: 0xd83a3a,
    projectile: {
      speed: 16, gravity: 1, radius: 0.14, fuse: 1.8, bounce: 0, sticky: true, impactDamage: 0, visual: 'grenade',
      explosion: { radius: 3.2, damage: 115, voxelPower: 75, voxelRadius: 1.35, knockback: 12, selfDamage: 0.5 },
    },
  },
  {
    id: 'cluster', name: 'Cluster Grenade', description: 'Splits into four bomblets on detonation.', kind: 'cluster',
    cooldown: 18, unlockLevel: 15, price: 4000, currency: 'coins', icon: 'sparkles', color: 0xb44aff,
    projectile: {
      speed: 17, gravity: 1, radius: 0.14, fuse: 1.6, bounce: 0.35, sticky: false, impactDamage: 5, visual: 'grenade',
      explosion: { radius: 2.4, damage: 60, voxelPower: 40, voxelRadius: 0.9, knockback: 8, selfDamage: 0.5 },
      cluster: { count: 4, def: bomblet },
    },
  },
];

/**
 * Wire numbers (events, snapshots, projectile weapon ids 200 + num): never renumber. Number 6 was the Jet Burst, which let a player fly over the map walls; it is
 * retired and the number is never reused (old clients and recorded replays still carry it).
 */
const NUM: Record<string, number> = { frag: 1, medkit: 2, stim: 3, wall: 4, sticky: 5, cluster: 7 };
export const RETIRED_GADGETS: Record<string, { num: number; price: number }> = { jetpack: { num: 6, price: 2400 } };

export const GADGETS: GadgetDef[] = LIST.map((g) => ({ ...g, num: NUM[g.id] }));
export const GADGET_BY_ID: Record<string, GadgetDef> = Object.fromEntries(GADGETS.map((g) => [g.id, g]));
export const GADGET_BY_NUM: GadgetDef[] = [];
for (const g of GADGETS) GADGET_BY_NUM[g.num] = g;

export function gadget(id: string): GadgetDef {
  const g = GADGET_BY_ID[id];
  if (!g) throw new Error(`Unknown gadget ${id}`);
  return g;
}
